
import React from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { supabaseBikeService } from "@/api/supabaseBikeService";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DeleteBikeDialogProps {
  open: boolean;
  bikeId: string;
  bikeName: string;
  onOpenChange: (open: boolean) => void;
  onDelete: () => void;
  showDeleteToast?: boolean;
}

const DeleteBikeDialog: React.FC<DeleteBikeDialogProps> = ({
  open,
  bikeId,
  bikeName,
  onOpenChange,
  onDelete,
  showDeleteToast = true,
}) => {
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDelete = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await supabaseBikeService.deleteBike(bikeId);
      
      if (showDeleteToast) {
        toast.success(`eBike ${bikeName} has been deleted`);
      }
      
      onDelete();
    } catch (error) {
      console.error("Error deleting eBike:", error);
      toast.error("Failed to delete eBike. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(newOpen) => {
      if (isDeleting && newOpen === false) return; // Prevent closing while deleting
      onOpenChange(newOpen);
    }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <AlertDialogTitle>Delete eBike</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>{bikeName}</strong>? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDelete} 
            className="bg-destructive text-destructive-foreground"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteBikeDialog;
